import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, MoreHorizontal, Shield, Link as LinkIcon, UserPlus, KeyRound, Mail, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

type AppRole = "super_admin" | "restaurant_owner" | "manager" | "cashier" | "waiter" | "kitchen";

interface UserWithDetails {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  created_at: string;
  roles: {
    role: AppRole;
    restaurant_id: string | null;
    restaurant_name: string | null;
  }[];
}

interface Restaurant {
  id: string;
  name: string;
}

const roleLabels: Record<AppRole, string> = {
  super_admin: "Super Admin",
  restaurant_owner: "Proprietário",
  manager: "Gerente",
  cashier: "Caixa",
  waiter: "Garçom",
  kitchen: "Cozinha",
};

const roleBadgeColors: Record<AppRole, string> = {
  super_admin: "bg-purple-500",
  restaurant_owner: "bg-blue-500",
  manager: "bg-green-500",
  cashier: "bg-yellow-500",
  waiter: "bg-gray-500",
  kitchen: "bg-orange-500",
};

const getPasswordStrength = (password: string) => {
  let strength = 0;
  if (password.length >= 6) strength++;
  if (password.length >= 8) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;
  return strength;
};

const getStrengthLabel = (strength: number) => {
  if (strength <= 1) return { label: "Fraca", color: "bg-red-500" };
  if (strength <= 2) return { label: "Regular", color: "bg-yellow-500" };
  if (strength <= 3) return { label: "Boa", color: "bg-blue-500" };
  return { label: "Forte", color: "bg-green-500" };
};

export default function AdminUsuariosPage() {
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  const [selectedRole, setSelectedRole] = useState<AppRole>("cashier");
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create user form state
  const [newUser, setNewUser] = useState({
    fullName: "",
    email: "",
    password: "",
    phone: "",
    sendWelcomeEmail: true,
  });

  // Edit user form state
  const [editUser, setEditUser] = useState({
    fullName: "",
    email: "",
    phone: "",
  });

  // Reset password state
  const [resetPassword, setResetPassword] = useState({
    password: "",
    sendEmail: true,
  });

  useEffect(() => {
    fetchUsers();
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    const { data, error } = await supabase
      .from("restaurants")
      .select("id, name")
      .order("name");

    if (error) {
      console.error("Error fetching restaurants:", error);
      return;
    }

    setRestaurants(data || []);
  };

  const fetchUsers = async () => {
    setIsLoading(true);

    // Fetch all profiles
    const { data: profiles, error: profilesError } = await supabase
      .from("profiles")
      .select("id, full_name, phone, created_at");

    if (profilesError) {
      console.error("Error fetching profiles:", profilesError);
      setIsLoading(false);
      return;
    }

    // Fetch user_roles
    const { data: userRoles, error: userRolesError } = await supabase
      .from("user_roles")
      .select("user_id, role");

    if (userRolesError) {
      console.error("Error fetching user_roles:", userRolesError);
    }

    // Fetch restaurant_users with restaurant names
    const { data: restaurantUsers, error: restaurantUsersError } = await supabase
      .from("restaurant_users")
      .select(`
        user_id,
        role,
        restaurant_id,
        restaurants:restaurant_id (name)
      `);

    if (restaurantUsersError) {
      console.error("Error fetching restaurant_users:", restaurantUsersError);
    }

    // Fetch user emails from edge function
    let userEmails: Record<string, string> = {};
    try {
      const { data: emailData, error: emailError } = await supabase.functions.invoke("list-users");
      if (emailError) {
        console.error("Error fetching user emails:", emailError);
      } else if (emailData?.userEmails) {
        userEmails = emailData.userEmails;
      }
    } catch (error) {
      console.error("Error calling list-users function:", error);
    }

    // Build user list with roles
    const usersWithDetails: UserWithDetails[] = profiles.map((profile) => {
      const roles: UserWithDetails["roles"] = [];

      // Add super_admin roles from user_roles
      const superAdminRole = userRoles?.find(
        (ur) => ur.user_id === profile.id && ur.role === "super_admin"
      );
      if (superAdminRole) {
        roles.push({
          role: "super_admin",
          restaurant_id: null,
          restaurant_name: null,
        });
      }

      // Add restaurant-specific roles
      const userRestaurantRoles = restaurantUsers?.filter(
        (ru) => ru.user_id === profile.id
      );
      userRestaurantRoles?.forEach((rur) => {
        roles.push({
          role: rur.role as AppRole,
          restaurant_id: rur.restaurant_id,
          restaurant_name: (rur.restaurants as any)?.name || null,
        });
      });

      return {
        id: profile.id,
        email: userEmails[profile.id] || "Email não disponível",
        full_name: profile.full_name,
        phone: profile.phone,
        created_at: profile.created_at,
        roles,
      };
    });

    setUsers(usersWithDetails);
    setIsLoading(false);
  };

  const handleAddSuperAdmin = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    const { error } = await supabase.from("user_roles").insert({
      user_id: selectedUser.id,
      role: "super_admin",
    });

    if (error) {
      toast.error("Erro ao adicionar permissão de super admin");
      console.error(error);
    } else {
      toast.success("Permissão de super admin adicionada com sucesso");
      fetchUsers();
    }

    setRoleDialogOpen(false);
    setIsSubmitting(false);
  };

  const handleRemoveSuperAdmin = async (userId: string) => {
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .eq("role", "super_admin");

    if (error) {
      toast.error("Erro ao remover permissão de super admin");
      console.error(error);
    } else {
      toast.success("Permissão de super admin removida com sucesso");
      fetchUsers();
    }
  };

  const handleLinkToRestaurant = async () => {
    if (!selectedUser || !selectedRestaurant) return;

    setIsSubmitting(true);

    // Check if user is already linked to this restaurant
    const existingLink = selectedUser.roles.find(
      (r) => r.restaurant_id === selectedRestaurant
    );

    if (existingLink) {
      // Update existing role
      const { error } = await supabase
        .from("restaurant_users")
        .update({ role: selectedRole })
        .eq("user_id", selectedUser.id)
        .eq("restaurant_id", selectedRestaurant);

      if (error) {
        toast.error("Erro ao atualizar cargo do usuário");
        console.error(error);
      } else {
        toast.success("Cargo atualizado com sucesso");
        fetchUsers();
      }
    } else {
      // Create new link
      const { error } = await supabase.from("restaurant_users").insert({
        user_id: selectedUser.id,
        restaurant_id: selectedRestaurant,
        role: selectedRole,
      });

      if (error) {
        toast.error("Erro ao vincular usuário ao restaurante");
        console.error(error);
      } else {
        toast.success("Usuário vinculado ao restaurante com sucesso");
        fetchUsers();
      }
    }

    setLinkDialogOpen(false);
    setSelectedRestaurant("");
    setSelectedRole("cashier");
    setIsSubmitting(false);
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.fullName) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast.error("Email inválido");
      return;
    }

    // Validate password strength
    if (newUser.password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await supabase.functions.invoke("create-user", {
        body: {
          email: newUser.email,
          password: newUser.password,
          fullName: newUser.fullName,
          phone: newUser.phone || null,
          sendWelcomeEmail: newUser.sendWelcomeEmail,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success("Usuário criado com sucesso");
      setCreateDialogOpen(false);
      setNewUser({
        fullName: "",
        email: "",
        password: "",
        phone: "",
        sendWelcomeEmail: true,
      });
      fetchUsers();
    } catch (error: any) {
      console.error("Error creating user:", error);
      toast.error(error.message || "Erro ao criar usuário");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    if (!editUser.fullName.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    // Validate email format if changed
    if (editUser.email && editUser.email !== selectedUser.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editUser.email)) {
        toast.error("Email inválido");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const response = await supabase.functions.invoke("update-user", {
        body: {
          userId: selectedUser.id,
          email: editUser.email !== selectedUser.email ? editUser.email : undefined,
          fullName: editUser.fullName,
          phone: editUser.phone || null,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success("Usuário atualizado com sucesso");
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast.error(error.message || "Erro ao atualizar usuário");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !resetPassword.password) {
      toast.error("Preencha a nova senha");
      return;
    }

    if (resetPassword.password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await supabase.functions.invoke("reset-user-password", {
        body: {
          userId: selectedUser.id,
          newPassword: resetPassword.password,
          sendEmail: resetPassword.sendEmail,
          userEmail: selectedUser.email,
          userName: selectedUser.full_name,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success("Senha redefinida com sucesso");
      setResetPasswordDialogOpen(false);
      setResetPassword({ password: "", sendEmail: true });
      setSelectedUser(null);
    } catch (error: any) {
      console.error("Error resetting password:", error);
      toast.error(error.message || "Erro ao redefinir senha");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (user: UserWithDetails) => {
    setSelectedUser(user);
    setEditUser({
      fullName: user.full_name || "",
      email: user.email !== "Email não disponível" ? user.email : "",
      phone: user.phone || "",
    });
    setEditDialogOpen(true);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.phone?.includes(search)
  );

  const passwordStrength = getPasswordStrength(newUser.password);
  const strengthInfo = getStrengthLabel(passwordStrength);

  const resetPasswordStrength = getPasswordStrength(resetPassword.password);
  const resetStrengthInfo = getStrengthLabel(resetPasswordStrength);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Usuários</h1>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuários</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, email ou telefone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando usuários...
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Cargos</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Nenhum usuário encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.full_name || "Sem nome"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{user.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>{user.phone || "-"}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.roles.length === 0 ? (
                              <Badge variant="outline">Sem cargo</Badge>
                            ) : (
                              user.roles.map((role, idx) => (
                                <Badge
                                  key={idx}
                                  className={`${roleBadgeColors[role.role]} text-white`}
                                >
                                  {roleLabels[role.role]}
                                  {role.restaurant_name && (
                                    <span className="ml-1 opacity-75">
                                      ({role.restaurant_name})
                                    </span>
                                  )}
                                </Badge>
                              ))
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {format(new Date(user.created_at), "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar Usuário
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {!user.roles.some((r) => r.role === "super_admin") ? (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setRoleDialogOpen(true);
                                  }}
                                >
                                  <Shield className="mr-2 h-4 w-4" />
                                  Tornar Super Admin
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => handleRemoveSuperAdmin(user.id)}
                                  className="text-destructive"
                                >
                                  <Shield className="mr-2 h-4 w-4" />
                                  Remover Super Admin
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setLinkDialogOpen(true);
                                }}
                              >
                                <LinkIcon className="mr-2 h-4 w-4" />
                                Vincular a Restaurante
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setResetPasswordDialogOpen(true);
                                }}
                              >
                                <KeyRound className="mr-2 h-4 w-4" />
                                Redefinir Senha
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input
                value={editUser.fullName}
                onChange={(e) =>
                  setEditUser({ ...editUser, fullName: e.target.value })
                }
                placeholder="Nome do usuário"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editUser.email}
                onChange={(e) =>
                  setEditUser({ ...editUser, email: e.target.value })
                }
                placeholder="email@exemplo.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input
                value={editUser.phone}
                onChange={(e) =>
                  setEditUser({ ...editUser, phone: e.target.value })
                }
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditUser} disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Super Admin Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Super Admin</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Tem certeza que deseja tornar{" "}
            <strong>{selectedUser?.full_name || selectedUser?.email}</strong> um
            Super Admin? Esta permissão dá acesso total ao sistema.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddSuperAdmin} disabled={isSubmitting}>
              {isSubmitting ? "Adicionando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to Restaurant Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular a Restaurante</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Vincular{" "}
              <strong>{selectedUser?.full_name || selectedUser?.email}</strong> a
              um restaurante.
            </p>

            <div className="space-y-2">
              <Label>Restaurante</Label>
              <Select
                value={selectedRestaurant}
                onValueChange={setSelectedRestaurant}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um restaurante" />
                </SelectTrigger>
                <SelectContent>
                  {restaurants.map((restaurant) => (
                    <SelectItem key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Cargo</Label>
              <Select
                value={selectedRole}
                onValueChange={(v) => setSelectedRole(v as AppRole)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="restaurant_owner">Proprietário</SelectItem>
                  <SelectItem value="manager">Gerente</SelectItem>
                  <SelectItem value="cashier">Caixa</SelectItem>
                  <SelectItem value="waiter">Garçom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleLinkToRestaurant}
              disabled={isSubmitting || !selectedRestaurant}
            >
              {isSubmitting ? "Vinculando..." : "Vincular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input
                value={newUser.fullName}
                onChange={(e) =>
                  setNewUser({ ...newUser, fullName: e.target.value })
                }
                placeholder="Nome do usuário"
              />
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                placeholder="email@exemplo.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Senha *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
                placeholder="Mínimo 6 caracteres"
              />
              {newUser.password && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${strengthInfo.color} transition-all`}
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {strengthInfo.label}
                    </span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    <li className={newUser.password.length >= 6 ? "text-green-600" : ""}>
                      ✓ Mínimo 6 caracteres
                    </li>
                    <li className={/[A-Z]/.test(newUser.password) ? "text-green-600" : ""}>
                      ✓ Uma letra maiúscula
                    </li>
                    <li className={/[0-9]/.test(newUser.password) ? "text-green-600" : ""}>
                      ✓ Um número
                    </li>
                    <li className={/[^A-Za-z0-9]/.test(newUser.password) ? "text-green-600" : ""}>
                      ✓ Um caractere especial
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input
                value={newUser.phone}
                onChange={(e) =>
                  setNewUser({ ...newUser, phone: e.target.value })
                }
                placeholder="(00) 00000-0000"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendWelcomeEmail"
                checked={newUser.sendWelcomeEmail}
                onCheckedChange={(checked) =>
                  setNewUser({ ...newUser, sendWelcomeEmail: checked as boolean })
                }
              />
              <Label htmlFor="sendWelcomeEmail" className="text-sm font-normal">
                Enviar email de boas-vindas com credenciais
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} disabled={isSubmitting}>
              {isSubmitting ? "Criando..." : "Criar Usuário"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Redefinir Senha</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Redefinir senha de{" "}
              <strong>{selectedUser?.full_name || selectedUser?.email}</strong>
            </p>

            <div className="space-y-2">
              <Label>Nova Senha *</Label>
              <Input
                type="password"
                value={resetPassword.password}
                onChange={(e) =>
                  setResetPassword({ ...resetPassword, password: e.target.value })
                }
                placeholder="Mínimo 6 caracteres"
              />
              {resetPassword.password && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${resetStrengthInfo.color} transition-all`}
                        style={{ width: `${(resetPasswordStrength / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {resetStrengthInfo.label}
                    </span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    <li className={resetPassword.password.length >= 6 ? "text-green-600" : ""}>
                      ✓ Mínimo 6 caracteres
                    </li>
                    <li className={/[A-Z]/.test(resetPassword.password) ? "text-green-600" : ""}>
                      ✓ Uma letra maiúscula
                    </li>
                    <li className={/[0-9]/.test(resetPassword.password) ? "text-green-600" : ""}>
                      ✓ Um número
                    </li>
                    <li className={/[^A-Za-z0-9]/.test(resetPassword.password) ? "text-green-600" : ""}>
                      ✓ Um caractere especial
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendResetEmail"
                checked={resetPassword.sendEmail}
                onCheckedChange={(checked) =>
                  setResetPassword({ ...resetPassword, sendEmail: checked as boolean })
                }
              />
              <Label htmlFor="sendResetEmail" className="text-sm font-normal">
                Enviar email com nova senha
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} disabled={isSubmitting}>
              {isSubmitting ? "Redefinindo..." : "Redefinir Senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
